<script setup>
import axios from 'axios'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon
} from '@heroicons/vue/20/solid'
import {
  DocumentArrowUpIcon,
  CloudArrowUpIcon
} from '@heroicons/vue/24/outline'
import { onMounted, reactive, ref, computed } from 'vue'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import isBetween from 'dayjs/plugin/isBetween'
import Swal from 'sweetalert2'
import Dialog from 'primevue/dialog'
import ProgressBar from 'primevue/progressbar'
import Calendar from 'primevue/calendar'
import format34 from '@/helpers/format34'
import { formatDatetime } from '../functions/format_works.js'

dayjs.extend(updateLocale)
dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.extend(isBetween)
dayjs.extend(format34)
dayjs.updateLocale('en', {
  weekStart: 1,
  weekdaysShort: ['日', '月', '火', '水', '木', '金', '土']
})
const props = defineProps({
  department: {
    type: Object,
    required: true
  },
  creators: {
    type: Object,
    required: true
  },
  initWorkPeriod: {
    type: Object,
    required: true
  },
  workPeriods: {
    type: Array,
    required: true
  },
  shiftOptions: {
    type: Array,
    required: true
  },
  currentUser: {
    type: Object,
    required: true
  }
})
const selectedCreator = ref([])
const selectedCreatorAttributes = computed(() => {
  return selectedCreator.value.map((selected) => {
    const target = data.fetched.creator_works.find(
      (c) => c.creator_id === selected.id
    )

    return target
  })
})
const countShift = (creator, shift) => {
  const result = creator.works.filter((work) => {
    return (
      work.shift_time_type_id === shift.id &&
      dayjs(work.date).isBetween(
        selectedWorkPeriod.value.firstDate,
        selectedWorkPeriod.value.lastDate,
        'day',
        []
      )
    )
  })
  return result.length
}

const selectedWorkPeriod = ref({
  id: props.initWorkPeriod.id,
  firstDate: props.initWorkPeriod.first_date,
  lastDate: props.initWorkPeriod.last_date,
  expiredAt: props.initWorkPeriod.expired_at
})
const selectedDate = ref()
const data = reactive({
  fetched: {},
  creators: {},
  targetWorks: [],
  isLoading: false,
  isEmpty: false,
  hasLockedWork: false,
  displayedStatus: 'latest'
})
const dateData = reactive({
  startOfCalendar: {},
  endOfCalendar: {},
  displayedStartOfCalendar: {},
  displayedEndOfCalendar: {},
  displayDates: []
})
const setData = () => {
  data.creators = props.creators
  data.isEmpty =
    props.creators['first_date'] === null || props.workPeriods.length === 0
  dateData.startOfCalendar = dayjs(props.initWorkPeriod['first_date']).startOf(
    'week'
  )
  dateData.endOfCalendar = dayjs(props.initWorkPeriod['last_date'])
    .add(1, 'w')
    .startOf('week')
  dateData.displayedStartOfCalendar = (
    dayjs() >= dayjs(props.initWorkPeriod['first_date'])
      ? dayjs()
      : dayjs(props.initWorkPeriod['first_date'])
  ).startOf('week')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
}
const fetchWorks = async () => {
  let result = await axios.get(
    `/departments/${props.department.id}/works.json\
      ?start_date=${dateData.startOfCalendar.format('YYYY-MM-DD')}\
      &end_date=${dateData.endOfCalendar.format('YYYY-MM-DD')}`
  )
  data.fetched = result.data
}
const dateSelected = () => {
  const foundWorkPeriod = props.workPeriods.find((wp) => {
    if (
      dayjs(wp.first_date) <= dayjs(selectedDate.value) &&
      dayjs(selectedDate.value) <= dayjs(wp.last_date)
    ) {
      return true
    }
  })

  if (foundWorkPeriod) {
    selectedWorkPeriod.value = {
      id: foundWorkPeriod.id,
      firstDate: foundWorkPeriod.first_date,
      lastDate: foundWorkPeriod.last_date,
      expiredAt: foundWorkPeriod.expired_at
    }
    changeDisplayWorks()
  }
}
// 配列の順序が"新しい日付ほどindexが小さい"ことが前提になっています
const selectNextWorkPeriod = () => {
  const index = props.workPeriods.findIndex(
    (wp) => wp.id === selectedWorkPeriod.value.id
  )
  if (index - 1 >= 0) {
    const wp = props.workPeriods[index - 1]
    selectedWorkPeriod.value = {
      id: wp.id,
      firstDate: wp.first_date,
      lastDate: wp.last_date,
      expiredAt: wp.expired_at
    }
    changeDisplayWorks()
  }
}
const selectPrevWorkPeriod = () => {
  const index = props.workPeriods.findIndex(
    (wp) => wp.id === selectedWorkPeriod.value.id
  )
  if (index + 1 < props.workPeriods.length) {
    const wp = props.workPeriods[index + 1]
    selectedWorkPeriod.value = {
      id: wp.id,
      firstDate: wp.first_date,
      lastDate: wp.last_date,
      expiredAt: wp.expired_at
    }
    changeDisplayWorks()
  }
}
const changeDisplayWorks = async () => {
  data.isLoading = true
  dateData.startOfCalendar = dayjs(selectedWorkPeriod.value.firstDate).startOf(
    'week'
  )
  dateData.endOfCalendar = dayjs(selectedWorkPeriod.value.lastDate)
    .add(1, 'w')
    .startOf('week')
  dateData.displayedStartOfCalendar = dayjs(
    selectedWorkPeriod.value.firstDate
  ).startOf('week')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
  await fetchWorks()
  data.targetWorks = buildTargetWorks()
  await buildDisplayWorks()
  data.isLoading = false
  selectedDate.value = ''
}
const buildTargetWorks = () => {
  const result = []
  data.fetched.creator_works.forEach((creator_work) => {
    const display_works = []
    let indexDay = dateData.startOfCalendar.clone()
    while (indexDay.isSameOrBefore(dateData.endOfCalendar)) {
      if (
        creator_work.works.find(
          (work) => work.date === indexDay.format('YYYY-MM-DD')
        )
      )
        display_works.push(
          creator_work.works.find(
            (work) => work.date === indexDay.format('YYYY-MM-DD')
          )
        )
      else {
        display_works.push({
          date: indexDay.format('YYYY-MM-DD'),
          status: 'adjusting',
          offer_id: null,
          offer_note: '',
          entry_id: null,
          entry_note: '',
          shift_time_type_id: null,
          imported_at: null,
          latest_reservations: [],
          diff_reservations: [],
          weekly_reservations: []
        })
      }
      let isExpired = false
      const day = display_works.slice(-1)[0]
      props.workPeriods.forEach((workPeriod) => {
        if (
          dayjs(workPeriod.expired_at) < dayjs() &&
          dayjs(workPeriod.first_date) <= dayjs(day.date) &&
          dayjs(workPeriod.last_date) >= dayjs(day.date)
        )
          isExpired = true
      })
      display_works.slice(-1)[0]['is_expired'] = isExpired
      indexDay = indexDay.add(1, 'day')
    }
    result.push({
      creator_id: creator_work.creator_id,
      creator_url: `/creators/${creator_work.creator_id}/schedules`,
      schedule_url: `/creators/${creator_work.creator_id}/work_registrations/new`,
      ignore_expiration: creator_work.ignore_expiration,
      creator_name: creator_work.name,
      is_primary_department: creator_work.is_primary_department,
      works: display_works
    })
  })
  return result
}
const buildDisplayWorks = () => {
  dateData.displayDates = []
  let indexDay = dateData.displayedStartOfCalendar.clone()
  while (indexDay.isSameOrBefore(dateData.displayedEndOfCalendar)) {
    dateData.displayDates.push(indexDay.format('YYYY-MM-DD'))
    indexDay = indexDay.add(1, 'day')
  }
}
onMounted(async () => {
  data.isLoading = true
  await setData()
  await fetchWorks()
  data.targetWorks = buildTargetWorks()
  await buildDisplayWorks()
  data.isLoading = false
})
const isDisabledPreviousButton = () =>
  dayjs(dateData.displayDates[0]) <= dateData.startOfCalendar
const isDisabledNextButton = () =>
  dayjs(dateData.displayDates.slice(-1)[0]) >= dateData.endOfCalendar
const isRegistrationInvalidDay = (day) => {
  return (
    dayjs(day.date).isBefore(dayjs(), 'day') ||
    dayjs(day.date).isBefore(
      dayjs(selectedWorkPeriod.value['firstDate']),
      'day'
    ) ||
    dayjs(selectedWorkPeriod.value['lastDate']).isBefore(dayjs(day.date), 'day')
  )
}
const setPreviousDisplayWorks = () => {
  dateData.displayedStartOfCalendar =
    dateData.displayedStartOfCalendar.subtract(7, 'day')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
const setNextDisplayWorks = () => {
  dateData.displayedStartOfCalendar = dateData.displayedStartOfCalendar.add(
    7,
    'day'
  )
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
const setInitialDisplayWorks = () => {
  dateData.displayedStartOfCalendar = dayjs(
    selectedWorkPeriod.value.firstDate
  ).startOf('week')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
const filterWorks = (works) =>
  works.filter(
    (work) =>
      dayjs(work.date) >= dateData.displayedStartOfCalendar &&
      dayjs(work.date) <= dateData.displayedEndOfCalendar
  )
const importedWork = (creator_works, selectedWorkPeriod) => {
  return creator_works.works.find(
    (work) =>
      dayjs(selectedWorkPeriod.firstDate) <= dayjs(work.date) &&
      dayjs(work.date) <= dayjs(selectedWorkPeriod.lastDate) &&
      work?.imported_at
  )
}
const lock = async () => {
  const params = {
    creator_ids: selectedCreator.value.map((creator) => creator['id']),
    work_period_id: selectedWorkPeriod.value.id,
    department_id: props.department.id
  }
  if (selectedCreator.value.length === 0) {
    const modal = await Swal.fire({
      title: 'クリエイターを選択してください',
      text: '',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      focusCancel: true
    })
    modal.isConfirmed
  } else {
    const modal = await Swal.fire({
      title: 'ロックしてもよろしいですか？',
      text: '',
      confirmButtonText: 'はい',
      confirmButtonColor: '#40BF6C',
      cancelButtonText: 'キャンセル',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true
    })
    if (modal.isConfirmed) {
      data.isLoading = true
      try {
        await axios.delete('/ignore_expirations', { data: params })
        data.isLoading = false
        await Swal.fire({
          icon: 'success',
          title: 'ロックが完了しました',
          text: '',
          confirmButtonText: 'OK',
          confirmButtonColor: '#40BF6C',
          reverseButtons: true,
          allowOutsideClick: false
        })
      } catch (error) {
        data.isLoading = false
        await Swal.fire({
          icon: 'error',
          title: 'ロックに失敗しました',
          text: '管理者にお問合せください',
          confirmButtonText: 'OK',
          confirmButtonColor: '#40BF6C',
          reverseButtons: true,
          allowOutsideClick: false
        })
      }
      await refetchWorks()
    }
  }
}
const unlock = async () => {
  const params = {
    creator_ids: selectedCreator.value.map((creator) => creator['id']),
    work_period_id: selectedWorkPeriod.value.id,
    department_id: props.department.id
  }
  if (
    selectedCreator.value.length === 0 ||
    isNaN(Number(selectedWorkPeriod.value.id))
  ) {
    const modal = await Swal.fire({
      title: 'クリエイターと作業日期間を選択してください',
      text: '',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      focusCancel: true
    })
    modal.isConfirmed
  } else {
    const modal = await Swal.fire({
      title: 'ロック解除してもよろしいですか？',
      text: '',
      confirmButtonText: 'はい',
      confirmButtonColor: '#40BF6C',
      cancelButtonText: 'キャンセル',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true
    })
    if (modal.isConfirmed) {
      data.isLoading = true
      try {
        await axios.post('/ignore_expirations', params)
        data.isLoading = false
        await Swal.fire({
          icon: 'success',
          title: 'ロック解除が完了しました',
          text: '',
          confirmButtonText: 'OK',
          confirmButtonColor: '#40BF6C',
          reverseButtons: true,
          allowOutsideClick: false
        })
      } catch (error) {
        data.isLoading = false
        await Swal.fire({
          icon: 'error',
          title: 'ロック解除に失敗しました',
          text: '管理者にお問合せください',
          confirmButtonText: 'OK',
          confirmButtonColor: '#40BF6C',
          reverseButtons: true,
          allowOutsideClick: false
        })
      }
      await refetchWorks()
    }
  }
}

const monthlySchedule = ref({
  displayModal: false,
  showProgress: false,
  buttonDisabled: false,
  needsEmailNotification: false
})
const fixMonthlySchedule = async () => {
  const isUnlocked = selectedCreatorAttributes.value.map((creator) => {
    if (creator.ignore_expiration) return true
    return dayjs(selectedWorkPeriod.value.expiredAt) >= dayjs()
  })
  let errorMeessage = ''
  if (selectedCreator.value.length === 0) {
    errorMeessage = 'クリエイターを選択してください'
  } else if (isUnlocked.includes(true)) {
    errorMeessage = 'ロック済みのクリエイターのみを選択してください'
  }
  if (errorMeessage !== '') {
    const modal = await Swal.fire({
      title: errorMeessage,
      text: '',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      focusCancel: true
    })
    modal.isConfirmed
    return
  }

  monthlySchedule.value.displayModal = true
}
const monthlySchedulePayload = computed(() => {
  const schedules = selectedCreator.value.map((c) => {
    return { creator_id: c.id, work_period_id: selectedWorkPeriod.value.id }
  })
  return {
    monthly_schedule_registration: {
      monthly_schedules: schedules,
      work_period_id: selectedWorkPeriod.value.id,
      needs_email_notification: monthlySchedule.value.needsEmailNotification
    }
  }
})
const postMonthlySchedule = async (sendEmail) => {
  monthlySchedule.value.needsEmailNotification = sendEmail
  monthlySchedule.value.showProgress = true
  monthlySchedule.value.buttonDisabled = true
  try {
    const response = await axios.post(
      `/departments/${props.department.id}/monthly_schedules`,
      monthlySchedulePayload.value
    )
    console.log(response)
    const status = response.status
    if (status !== 200) {
      throw Error('post monthly schedule failed')
    }

    closeMonthlyScheduleModal()
    monthlySchedule.value.showProgress = false
    monthlySchedule.value.buttonDisabled = false
    const message = monthlySchedule.value.needsEmailNotification
      ? '数分以内に通知メールが送信されます'
      : 'スケジュール調整メニューの月間スケジュールよりPDFをダウンロードできます'
    await Swal.fire({
      icon: 'success',
      title: '月間確定が完了しました',
      text: message,
      confirmButtonText: '戻る',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
  } catch (error) {
    monthlySchedule.value.showProgress = false
    monthlySchedule.value.buttonDisabled = false
    console.error(error)
    closeMonthlyScheduleModal()
    await Swal.fire({
      icon: 'error',
      title: '月間確定処理に失敗しました',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
  } finally {
    monthlySchedule.value.showProgress = false
    monthlySchedule.value.buttonDisabled = false
    closeMonthlyScheduleModal()
    await refetchWorks()
  }
}
const closeMonthlyScheduleModal = () => {
  monthlySchedule.value.displayModal = false
}

const selectedFirstDate = ref('')
const selectedLastDate = ref('')
const creatorReservations = ref([])
const weeklySchedule = ref({
  displayModal: false,
  showProgress: false,
  buttonDisabled: false,
  needsEmailNotification: false
})
const countReservation = () => {
  creatorReservations.value = selectedCreatorAttributes.value.map((creator) => {
    let creatorWorks = creator.works.filter((work) => {
      return (
        work.latest_reservations?.length !== 0 &&
        dayjs(work.date).isBetween(
          dayjs(selectedFirstDate.value),
          dayjs(selectedLastDate.value),
          'day',
          []
        )
      )
    })
    return {
      creatorId: creator.creator_id,
      reservationCount: creatorWorks
        .map((work) => work.latest_reservations)
        .flat().length
    }
  })
}
const fixWeeklySchedule = async () => {
  if (selectedCreator.value.length === 0) {
    const modal = await Swal.fire({
      title: 'クリエイターを選択してください',
      text: '',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      focusCancel: true
    })
    modal.isConfirmed
    return
  }

  weeklySchedule.value.displayModal = true
}
const weeklySchedulePayload = computed(() => {
  const schedules = selectedCreator.value.map((c) => {
    return {
      creator_id: c.id,
      has_reservations:
        creatorReservations.value.find(
          (creatorReservation) => creatorReservation.creatorId === c.id
        ).reservationCount !== 0
    }
  })
  return {
    weekly_schedule_registration: {
      weekly_schedules: schedules,
      first_date: dayjs(selectedFirstDate.value).format('YYYY-MM-DD'),
      last_date: dayjs(selectedLastDate.value).format('YYYY-MM-DD'),
      needs_email_notification: weeklySchedule.value.needsEmailNotification
    }
  }
})
const postWeeklySchedule = async (sendEmail) => {
  weeklySchedule.value.needsEmailNotification = sendEmail
  weeklySchedule.value.showProgress = true
  weeklySchedule.value.buttonDisabled = true
  let errorMeessage = ''
  if (selectedFirstDate.value === '' || selectedLastDate.value === '') {
    errorMeessage = '期間を指定してください'
  } else if (dayjs(selectedFirstDate.value) > dayjs(selectedLastDate.value)) {
    errorMeessage = '終了日は開始日以降を指定してください'
  }
  if (errorMeessage !== '') {
    weeklySchedule.value.showProgress = false
    weeklySchedule.value.buttonDisabled = false
    closeWeeklyScheduleModal()
    const modal = await Swal.fire({
      title: errorMeessage,
      text: '',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      focusCancel: true
    })
    modal.isConfirmed
    weeklySchedule.value.displayModal = true
  } else {
    try {
      const response = await axios.post(
        `/departments/${props.department.id}/weekly_schedules`,
        weeklySchedulePayload.value
      )
      console.log(response)
      const status = response.status
      if (status !== 200) {
        throw Error('post weekly schedule failed')
      }

      closeWeeklyScheduleModal()
      weeklySchedule.value.showProgress = false
      weeklySchedule.value.buttonDisabled = false
      const message = weeklySchedule.value.needsEmailNotification
        ? '数分以内に通知メールが送信されます'
        : 'スケジュール調整メニューの週間スケジュールよりPDFをダウンロードできます'
      await Swal.fire({
        icon: 'success',
        title: '週間確定が完了しました',
        text: message,
        confirmButtonText: '戻る',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    } catch (error) {
      weeklySchedule.value.showProgress = false
      weeklySchedule.value.buttonDisabled = false
      console.error(error)
      closeWeeklyScheduleModal()
      await Swal.fire({
        icon: 'error',
        title: '週間確定処理に失敗しました',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    } finally {
      weeklySchedule.value.showProgress = false
      weeklySchedule.value.buttonDisabled = false
      closeWeeklyScheduleModal()
      await refetchWorks()
    }
  }
}
const closeWeeklyScheduleModal = () => {
  weeklySchedule.value.displayModal = false
}

const redirectCooperationSystemImport = () => {
  window.location.href = `/departments/${props.department.id}/reservations/new`
}
const excelData = reactive({
  displayDownloadModal: false,
  buttonDisabled: false,
  showProgress: false
})
const openDownloadModal = () => {
  excelData.displayDownloadModal = true
}
const closeDownloadModal = () => {
  excelData.displayDownloadModal = false
}
const downloadExcel = async () => {
  try {
    excelData.showProgress = true
    excelData.buttonDisabled = true
    const response = await axios.get(
      `/departments/${props.department.id}/excel`,
      {
        params: {
          start: selectedWorkPeriod.value.firstDate,
          end: selectedWorkPeriod.value.lastDate
        },
        responseType: 'blob'
      }
    )
    const link = document.createElement('a')
    const blobUrl = URL.createObjectURL(new Blob([response.data]))
    link.href = blobUrl
    link.download = `${dayjs(selectedWorkPeriod.value.firstDate).format(
      'YYYYMMDD'
    )}-${dayjs(selectedWorkPeriod.value.lastDate).format('YYMMDD')}_${
      props.department.name
    }.xlsx`
    document.body.appendChild(link)
    link.click()
    link.parentNode.removeChild(link)
    excelData.showProgress = false
    excelData.buttonDisabled = false
    closeDownloadModal()
  } catch (error) {
    console.error(error)
    excelData.showProgress = false
    excelData.buttonDisabled = false
    closeDownloadModal()
    await Swal.fire({
      icon: 'error',
      title: 'ダウンロードに失敗しました',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
  }
}
const redirectExcelImport = () => {
  window.location.href = `/departments/${props.department.id}/works/new?work_period_id=${selectedWorkPeriod.value.id}`
}
const dayBgColor = (day) => {
  if (isRegistrationInvalidDay(day)) {
    switch (day.status) {
      case 'adjusting':
        return 'bg-[#e4e2d0]'
      case 'monthly_fixed':
        return 'bg-[#d7dde5]'
      case 'weekly_fixed':
        return 'bg-[#d8e3db]'
      default:
        return 'bg-gray-50 text-gray-500'
    }
  }

  switch (day.status) {
    case 'adjusting':
      return 'bg-yellow-50'
    case 'monthly_fixed':
      return 'bg-blue-50'
    case 'weekly_fixed':
      return 'bg-green-50'
    default:
      return 'bg-gray-50 text-gray-500'
  }
}
const shiftBgColor = (day, type_id) => {
  if (day[type_id] == null) {
    return 'bg-gray-100'
  }
  if (type_id === 'offer_id') return 'bg-orange-300'
  if (type_id === 'entry_id') return 'bg-red-300'
}
const displayShift = (shiftId) => {
  const shift = props.shiftOptions.find(({ id }) => id === shiftId)
  if (shift) return shift.display_name

  return null
}
const refetchWorks = async () => {
  data.isLoading = true
  await fetchWorks()
  data.targetWorks = buildTargetWorks()
  await buildDisplayWorks()
  data.isLoading = false
}

const includeDiffReservations = (creatorWork) => {
  return creatorWork.some(
    (work) =>
      ![undefined, 0].includes(work.diff_reservations?.before_ids?.length) ||
      ![undefined, 0].includes(work.diff_reservations?.after_ids?.length)
  )
}
const toggleAllCreatorCheck = (e) => {
  if (e.target.checked) {
    selectedCreator.value.splice(0)
    selectedCreator.value.push(
      ...data.targetWorks
        .map((creator_works) => {
          if (!creator_works.is_primary_department) return false
          return {
            id: creator_works.creator_id,
            name: creator_works.creator_name
          }
        })
        .filter((item) => item)
    )
  } else {
    selectedCreator.value.splice(0)
  }
}
const isAllChecked = computed(() => {
  const allCount = data.targetWorks.filter((creator_works) => {
    return creator_works.is_primary_department
  }).length

  return selectedCreator.value.length === allCount
})
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <div class="relative flex h-full">
    <div class="flex h-full pb-6">
      <div class="mx-auto flex h-full px-4 sm:px-6 md:px-8">
        <div v-if="data.isEmpty" class="h-full lg:flex lg:h-full lg:flex-col">
          <header class="px-1 py-3">
            <div class="mb-8 flex items-center justify-between">
              <div class="text-2xl font-semibold text-gray-800">
                クリエイター一覧
              </div>
            </div>
          </header>
          <div class="mt-10 grid grid-cols-12">
            <div class="col-span-8 col-start-3">
              <div class="overflow-hidden rounded-lg bg-white px-6 py-5 shadow">
                <div
                  class="flex justify-center font-medium leading-6 text-gray-700"
                >
                  表示可能なスケジュールはございません。
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex h-full flex-1 flex-col">
          <div class="flex-none px-1 py-3">
            <div class="lg:flex lg:items-center lg:justify-between">
              <div class="min-w-0 flex-1">
                <div class="flex flex-row flex-wrap items-center">
                  <div class="text-2xl font-semibold text-gray-800">
                    クリエイター一覧
                  </div>
                  <div
                    class="mb-8 ml-3 mt-1 flex flex-col sm:mt-6 sm:flex-row sm:flex-wrap sm:space-x-8"
                  >
                    <div class="mt-2 flex items-center text-sm text-gray-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="mr-1 h-4 w-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
                        />
                      </svg>
                      {{ props.department.name }}
                    </div>
                  </div>
                  <div class="flex flex-row flex-wrap items-center">
                    <button
                      class="ml-4 h-8 justify-center rounded-l-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-300 hover:text-white"
                      @click="selectPrevWorkPeriod"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="h-4 w-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M15.75 19.5L8.25 12l7.5-7.5"
                        />
                      </svg>
                    </button>

                    <select
                      v-model="selectedWorkPeriod"
                      class="h-8 items-center border border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:text-gray-800"
                      @change="changeDisplayWorks"
                    >
                      <option
                        v-for="workPeriod in workPeriods"
                        v-show="
                          dayjs(workPeriod.last_date).isAfter(
                            dayjs().subtract(3, 'month')
                          )
                        "
                        :key="workPeriod.id"
                        :value="{
                          id: workPeriod.id,
                          firstDate: workPeriod.first_date,
                          lastDate: workPeriod.last_date,
                          expiredAt: workPeriod.expired_at
                        }"
                      >
                        {{
                          `${workPeriod.first_date} ~ ${workPeriod.last_date}`
                        }}
                      </option>
                    </select>
                    <button
                      class="h-8 items-center justify-center rounded-r-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-300 hover:text-white"
                      @click="selectNextWorkPeriod"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="h-4 w-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M8.25 4.5l7.5 7.5-7.5 7.5"
                        />
                      </svg>
                    </button>
                    <Calendar
                      v-model="selectedDate"
                      date-format="yy-mm-dd"
                      placeholder="日付を指定して移動"
                      @update:model-value="dateSelected"
                    ></Calendar>
                  </div>
                </div>
              </div>
              <div class="my-4">
                <button
                  v-if="props.currentUser['is_admin_or_scheduler'] === true"
                  class="rounded-md border border-transparent bg-yellow-500 px-2 py-1.5 text-sm text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2 lg:ml-6 lg:px-8 lg:text-base"
                  @click="openDownloadModal"
                >
                  <div class="flex gap-2">
                    <ArrowDownTrayIcon
                      class="mt-1 h-5 w-5"
                      aria-hidden="true"
                    />
                    <div>EXCELダウンロード</div>
                  </div>
                </button>
                <button
                  class="rounded-md border border-transparent bg-yellow-500 px-2 py-1.5 text-sm text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2 lg:ml-6 lg:px-8 lg:text-base"
                  @click="redirectExcelImport"
                >
                  <div class="flex gap-2">
                    <DocumentArrowUpIcon
                      class="mt-1 h-5 w-5"
                      aria-hidden="true"
                    />
                    <div>EXCELインポート</div>
                  </div>
                </button>
                <button
                  class="ml-6 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
                  @click="redirectCooperationSystemImport"
                >
                  <div class="flex gap-2">
                    <CloudArrowUpIcon class="mt-1 h-5 w-5" aria-hidden="true" />
                    <div>PANDAインポート</div>
                  </div>
                </button>
                <Dialog
                  v-model:visible="excelData.displayDownloadModal"
                  header="EXCELダウンロード"
                  :modal="true"
                >
                  <p class="m-0">
                    <span
                      class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
                      >期間</span
                    >
                    {{ selectedWorkPeriod.firstDate }} 〜
                    {{ selectedWorkPeriod.lastDate }}
                  </p>
                  <template #footer>
                    <button
                      class="group w-24 whitespace-nowrap rounded border border-gray-500 bg-white p-2.5 font-medium text-gray-800 hover:border-transparent hover:bg-gray-500 hover:text-white"
                      @click="closeDownloadModal"
                    >
                      キャンセル
                    </button>
                    <button
                      :class="[
                        excelData.buttonDisabled
                          ? 'disabled:opacity-25'
                          : 'hover:bg-green-500 hover:text-white',
                        'ml-4 w-24 rounded bg-green-300 p-2 p-2.5 px-4 font-bold text-gray-800'
                      ]"
                      :disabled="excelData.buttonDisabled"
                      @click="downloadExcel"
                    >
                      ダウンロード
                    </button>
                    <ProgressBar
                      v-if="excelData.showProgress"
                      mode="indeterminate"
                      style="height: 0.5em"
                      class="mt-4"
                    />
                  </template>
                </Dialog>
              </div>
            </div>
            <div class="lg:flex lg:justify-between">
              <div
                class="sm:flex md:ml-3 md:inline-block md:items-center lg:flex lg:items-center"
              >
                <div class="flex items-center">
                  <div
                    class="mr-2 flex items-center rounded-md shadow-sm md:items-stretch lg:h-8"
                  >
                    <button
                      class="flex items-center justify-center rounded-l-md border border-r-0 border-gray-300 bg-white py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                      :disabled="isDisabledPreviousButton()"
                      @click="setPreviousDisplayWorks"
                    >
                      <span class="sr-only">Previous month</span>
                      <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                    <button
                      class="hidden whitespace-nowrap border-b border-t border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:relative md:block"
                      @click="setInitialDisplayWorks"
                    >
                      初週
                    </button>
                    <span
                      class="relative -mx-px h-5 w-px bg-gray-300 md:hidden"
                    />
                    <button
                      :disabled="isDisabledNextButton()"
                      class="flex items-center justify-center rounded-r-md border border-l-0 border-gray-300 bg-white py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                      @click="setNextDisplayWorks"
                    >
                      <span class="sr-only">Next month</span>
                      <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div
                  class="hidden lg:ml-4 lg:flex lg:h-6 lg:w-px lg:bg-gray-300"
                />
                <div
                  class="flex flex-wrap items-center whitespace-nowrap text-xs md:flex md:items-center"
                >
                  <div class="py-3">
                    <div
                      class="ml-4 mr-2 inline-block h-3 w-3 rounded-full bg-orange-300"
                    ></div>
                    <span class="text-gray-700">Lab.提案</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="ml-5 mr-2 inline-block h-3 w-3 rounded-full bg-red-300"
                    ></div>
                    <span class="text-gray-700">クリエイター提出</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="ml-5 mr-2 inline-block h-3 w-3 rounded-full bg-blue-300"
                    ></div>
                    <span class="text-gray-700">EXCEL情報</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="ml-5 mr-2 inline-block h-3 w-3 rounded-full bg-green-300"
                    ></div>
                    <span class="text-gray-700">PANDA情報</span>
                  </div>
                  <div
                    class="ml-5 h-8 w-16 border border-gray-300 bg-yellow-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >調整中</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-blue-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >月確</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-green-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >週確</span
                    >
                  </div>
                </div>
              </div>
              <div
                class="flex-row sm:flex md:ml-3 md:inline-block md:items-center lg:flex lg:items-center"
              >
                <div class="lg:flex lg:items-center">
                  <input
                    id="adjusting"
                    v-model="data.displayedStatus"
                    class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                    type="radio"
                    value="adjusting"
                  />
                  <label class="mr-4 text-gray-700">調整中</label>
                  <input
                    id="excel"
                    v-model="data.displayedStatus"
                    class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                    type="radio"
                    value="excel"
                  />
                  <label class="mr-4 text-gray-700">EXCEL</label>
                  <input
                    id="weekly"
                    v-model="data.displayedStatus"
                    class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                    type="radio"
                    value="weekly"
                  />
                  <label class="mr-4 text-gray-700">週確</label>
                  <input
                    id="latest"
                    v-model="data.displayedStatus"
                    class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                    type="radio"
                    value="latest"
                  />
                  <label class="mr-4 text-gray-700">PANDA</label>
                  <button
                    v-if="props.currentUser['is_admin_or_scheduler'] === true"
                    :class="[
                      data.isEmpty
                        ? 'disabled:opacity-25'
                        : 'hover:bg-green-500 hover:text-white',
                      'mr-2 h-8 flex-row items-center justify-center rounded-md border border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 shadow-sm lg:flex lg:w-28'
                    ]"
                    :disabled="data.isEmpty"
                    @click="unlock"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="mr-1 h-3 w-3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                      />
                    </svg>
                    <span class="sr-only lg:not-sr-only">ロック解除</span>
                  </button>
                  <button
                    v-if="props.currentUser['is_admin_or_scheduler'] === true"
                    :class="[
                      data.isEmpty
                        ? 'disabled:opacity-25'
                        : 'hover:bg-red-400 hover:text-white',
                      'mr-2 h-8 flex-row items-center justify-center rounded-md border border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 shadow-sm lg:flex lg:w-28'
                    ]"
                    :disabled="data.isEmpty"
                    @click="lock"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="mr-1 h-3 w-3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                      />
                    </svg>
                    <span class="sr-only lg:not-sr-only">ロック</span>
                  </button>
                  <button
                    v-if="props.currentUser['is_admin_or_scheduler'] === true"
                    :class="[
                      data.isEmpty
                        ? 'disabled:opacity-25'
                        : 'hover:bg-blue-300 hover:text-white',
                      'mr-2 flex h-8 w-28 flex-row items-center justify-center rounded-md border border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 shadow-sm'
                    ]"
                    :disabled="data.isEmpty"
                    @click="fixMonthlySchedule"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="mr-1 h-3 w-3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                    月間確定
                  </button>
                  <Dialog
                    v-model:visible="monthlySchedule.displayModal"
                    header="月間確定"
                    :modal="true"
                    class="w-full lg:w-1/3"
                  >
                    <div class="flex items-center gap-2">
                      <dt
                        class="items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800"
                      >
                        期間
                      </dt>
                      <dd class="text-sm text-gray-900">
                        {{ selectedWorkPeriod.firstDate }} 〜
                        {{ selectedWorkPeriod.lastDate }}
                      </dd>
                    </div>
                    <div class="mt-4 flow-root py-4">
                      <div class="my-2">
                        <div class="inline-block min-w-full py-2 align-middle">
                          <table class="min-w-full divide-y divide-gray-300">
                            <thead>
                              <tr>
                                <th
                                  scope="col"
                                  class="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-0"
                                />
                                <th
                                  v-for="type in props.shiftOptions"
                                  :key="type.id"
                                  scope="col"
                                  class="py- 3 px-3 text-center text-xs uppercase text-gray-500"
                                >
                                  {{ type.display_name }}
                                </th>
                              </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200 bg-white">
                              <tr
                                v-for="creator in selectedCreatorAttributes"
                                :key="creator.creator_id"
                              >
                                <td
                                  class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0"
                                >
                                  {{ creator.name }}
                                </td>
                                <td
                                  v-for="type in props.shiftOptions"
                                  :key="type.id"
                                  class="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-500"
                                >
                                  {{ countShift(creator, type) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <template #footer>
                      <div class="mt-6 flex flex-wrap items-center justify-end">
                        <button
                          class="group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pl-2.5 pr-3 text-sm font-medium shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
                          @click="closeMonthlyScheduleModal"
                        >
                          <span
                            class="pl-1 text-gray-800 group-hover:text-white"
                            >キャンセル</span
                          >
                        </button>
                        <button
                          :class="[
                            monthlySchedule.buttonDisabled
                              ? 'disabled:opacity-25'
                              : '',
                            'group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pl-2.5 pr-3 text-sm font-medium shadow-sm hover:border-transparent  hover:bg-green-500 hover:text-white sm:w-auto'
                          ]"
                          :disabled="monthlySchedule.buttonDisabled"
                          @click="postMonthlySchedule(false)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="mr-1 h-3 w-3"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M4.5 12.75l6 6 9-13.5"
                            />
                          </svg>
                          <span
                            class="pl-1 text-gray-800 group-hover:text-white"
                            >月間確定のみ</span
                          >
                        </button>
                        <button
                          :class="[
                            monthlySchedule.buttonDisabled
                              ? 'disabled:opacity-25'
                              : '',
                            'group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pl-2.5 pr-3 text-sm font-medium shadow-sm hover:border-transparent  hover:bg-green-500 hover:text-white sm:w-auto'
                          ]"
                          :disabled="monthlySchedule.buttonDisabled"
                          @click="postMonthlySchedule(true)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="h-4 w-4 text-gray-400 group-hover:text-white"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                            />
                          </svg>
                          <span
                            class="pl-1 text-gray-800 group-hover:text-white"
                            >メール通知して月間確定</span
                          >
                        </button>
                      </div>
                      <ProgressBar
                        v-if="monthlySchedule.showProgress"
                        mode="indeterminate"
                        style="height: 0.5em"
                        class="mt-4"
                      />
                    </template>
                  </Dialog>
                  <button
                    v-if="props.currentUser['is_admin_or_scheduler'] === true"
                    :class="[
                      data.isEmpty
                        ? 'disabled:opacity-25'
                        : 'hover:bg-green-300 hover:text-white',
                      'mr-2 flex h-8 w-28 flex-row items-center justify-center rounded-md border border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 shadow-sm'
                    ]"
                    :disabled="data.isEmpty"
                    @click="fixWeeklySchedule"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="mr-1 h-3 w-3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                    週間確定
                  </button>
                  <Dialog
                    v-model:visible="weeklySchedule.displayModal"
                    header="週間確定"
                    :modal="true"
                    class="w-full lg:w-1/3"
                  >
                    <div class="flex grid grid-cols-6 items-center gap-2">
                      <div class="col-span-1 col-start-1 flex justify-center">
                        <div
                          class="items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800"
                        >
                          期間
                        </div>
                      </div>
                      <div
                        class="col-span-5 col-start-2 ml-2 flex items-center"
                      >
                        <Calendar
                          v-model="selectedFirstDate"
                          class="mr-2 h-10"
                          placeholder="開始日"
                          @date-select="countReservation"
                        />
                        〜
                        <Calendar
                          v-model="selectedLastDate"
                          class="mx-2 h-10"
                          placeholder="終了日"
                          @date-select="countReservation"
                        />
                      </div>
                    </div>
                    <div class="my-4 flow-root gap-2 px-8">
                      <div class="inline-block min-w-full py-2 align-middle">
                        <table class="min-w-full divide-y divide-gray-300">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                class="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-0"
                              />
                              <th
                                scope="col"
                                class="py- 3 px-3 text-center text-xs uppercase text-gray-500"
                              >
                                予約件数
                              </th>
                            </tr>
                          </thead>
                          <tbody class="divide-y divide-gray-200 bg-white">
                            <tr
                              v-for="creator in selectedCreatorAttributes"
                              :key="creator.creator_id"
                            >
                              <td
                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0"
                              >
                                {{ creator.name }}
                              </td>
                              <td
                                class="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-500"
                              >
                                {{
                                  creatorReservations.find(
                                    (creatorReservation) =>
                                      creatorReservation.creatorId ===
                                      creator.creator_id
                                  )?.reservationCount || 0
                                }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <template #footer>
                      <div class="mt-6 flex flex-wrap items-center justify-end">
                        <button
                          class="group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pl-2.5 pr-3 text-sm font-medium shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
                          @click="closeWeeklyScheduleModal"
                        >
                          <span
                            class="pl-1 text-gray-800 group-hover:text-white"
                            >キャンセル</span
                          >
                        </button>
                        <button
                          :class="[
                            weeklySchedule.buttonDisabled
                              ? 'disabled:opacity-25'
                              : '',
                            'group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pl-2.5 pr-3 text-sm font-medium shadow-sm hover:border-transparent  hover:bg-green-500 hover:text-white sm:w-auto'
                          ]"
                          :disabled="weeklySchedule.buttonDisabled"
                          @click="postWeeklySchedule(false)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="mr-1 h-3 w-3"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M4.5 12.75l6 6 9-13.5"
                            />
                          </svg>
                          <span
                            class="pl-1 text-gray-800 group-hover:text-white"
                            >週間確定のみ</span
                          >
                        </button>
                        <button
                          :class="[
                            weeklySchedule.buttonDisabled
                              ? 'disabled:opacity-25'
                              : '',
                            'group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pl-2.5 pr-3 text-sm font-medium shadow-sm hover:border-transparent  hover:bg-green-500 hover:text-white sm:w-auto'
                          ]"
                          :disabled="weeklySchedule.buttonDisabled"
                          @click="postWeeklySchedule(true)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="h-4 w-4 text-gray-400 group-hover:text-white"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                            />
                          </svg>
                          <span
                            class="pl-1 text-gray-800 group-hover:text-white"
                            >メール通知して週間確定</span
                          >
                        </button>
                      </div>
                      <ProgressBar
                        v-if="weeklySchedule.showProgress"
                        mode="indeterminate"
                        style="height: 0.5em"
                        class="mt-4"
                      />
                    </template>
                  </Dialog>
                </div>
              </div>
            </div>
          </div>
          <div
            class="flex h-auto overflow-auto shadow ring-1 ring-gray-300 ring-opacity-80 lg:flex-col"
          >
            <div
              class="sr-only grid flex-none grid-cols-9 gap-px border-gray-300 border-opacity-80 bg-gray-300 bg-opacity-80 text-center text-xs font-semibold leading-6 text-gray-700 lg:not-sr-only"
            >
              <div class="bg-white py-2">
                <input
                  id=""
                  type="checkbox"
                  name=""
                  class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 underline drop-shadow"
                  :checked="isAllChecked"
                  @change="toggleAllCreatorCheck"
                />
                <label for="" class="text-lg font-normal">全選択</label>
              </div>
              <div class="bg-white py-2">月</div>
              <div class="bg-white py-2">火</div>
              <div class="bg-white py-2">水</div>
              <div class="bg-white py-2">木</div>
              <div class="bg-white py-2">金</div>
              <div class="bg-white py-2 text-blue-400">土</div>
              <div class="bg-white py-2 text-red-400">日</div>
              <div class="bg-white py-2">月</div>
            </div>
            <!-- カレンダー内側 -->
            <div class="hidden-scrollbar grow overflow-auto">
              <div
                v-for="creator_works in data.targetWorks"
                :key="creator_works.creator_name"
              >
                <div
                  class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto"
                >
                  <div class="w-full border-t lg:grid lg:grid-cols-9 lg:gap-px">
                    <div class="flex flex-col justify-between bg-white p-3">
                      <div class="flex justify-end">
                        <div>
                          <a
                            :href="creator_works.creator_url"
                            title="スケジュール"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              class="mr-1 h-6 w-6 text-gray-400 hover:text-gray-200"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                              />
                            </svg>
                          </a>
                        </div>
                        <div>
                          <a
                            :href="creator_works.schedule_url"
                            title="作業日登録"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              class="h-6 w-6 text-gray-400 hover:text-gray-200"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                              />
                            </svg>
                          </a>
                        </div>
                      </div>
                      <div class="flex text-lg">
                        <div class="flex flex-col justify-center">
                          <input
                            v-if="creator_works.is_primary_department"
                            :key="creator_works.creator_name"
                            v-model="selectedCreator"
                            :value="{
                              id: creator_works.creator_id,
                              name: creator_works.creator_name
                            }"
                            type="checkbox"
                            class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 underline drop-shadow"
                          />
                        </div>
                        <label>
                          {{ creator_works.creator_name }}
                        </label>
                      </div>
                      <div
                        v-if="
                          importedWork(creator_works, selectedWorkPeriod)
                            ?.imported_at
                        "
                        class="text-sm"
                      >
                        (EXCELインポート日時：
                        {{
                          dayjs(
                            importedWork(creator_works, selectedWorkPeriod)
                              ?.imported_at
                          ).format('M/D HH:mm')
                        }})
                      </div>
                      <div
                        v-if="
                          includeDiffReservations(
                            filterWorks(creator_works.works)
                          )
                        "
                        class="fle-row mt-3 flex text-base font-semibold text-red-400"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="mr-1 mt-1 h-5 w-5"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                          />
                        </svg>
                        週間確定されていない予約があります
                      </div>
                      <div class="flex flex-wrap justify-center">
                        <span
                          v-if="!creator_works.is_primary_department"
                          class="mx-2 my-1 inline-block rounded bg-gray-100 px-2.5 py-0.5 text-sm text-gray-800"
                          >サブ所属</span
                        >
                        <span
                          v-if="dayjs(selectedWorkPeriod.expiredAt) < dayjs()"
                          :class="[
                            creator_works.ignore_expiration
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700',
                            'mx-2 my-1 inline-block rounded px-2.5 py-0.5 text-sm font-semibold'
                          ]"
                        >
                          {{
                            creator_works.ignore_expiration
                              ? 'ロック解除中'
                              : 'ロック中'
                          }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-for="day in filterWorks(creator_works.works)"
                      :key="day.date"
                      :class="[
                        dayBgColor(day),
                        'relative flex px-3 py-2 lg:block'
                      ]"
                    >
                      <div
                        class="flex flex-col pr-3 lg:flex-row lg:justify-between lg:pr-0"
                      >
                        <time
                          :datetime="day.date"
                          :class="
                            dayjs(day.date).isToday()
                              ? 'flex h-6 w-6 items-center justify-center rounded-full bg-yellow-500 p-4 font-bold text-white'
                              : 'font-bold'
                          "
                        >
                          {{ dayjs(day.date).format('M/D') }}
                        </time>
                        <div
                          class="not-sr-only lg:sr-only"
                          :class="{
                            'text-blue-400': dayjs(day.date).day() === 6,
                            'text-red-400': dayjs(day.date).day() === 0
                          }"
                        >
                          {{ dayjs(day.date).format('ddd') }}
                        </div>
                        <!--履歴ボタン-->
                        <WorkHistory :day-id="day.id" />
                      </div>
                      <!--カレンダー1日分-->
                      <!--PANDAインポート後-->
                      <div
                        v-if="
                          data.displayedStatus === 'latest' &&
                          (day.status === 'weekly_fixed' ||
                            day.latest_reservations?.length !== 0)
                        "
                      >
                        <div
                          v-for="reservation in day.diff_reservations
                            ?.reservations"
                          :key="reservation.start_time"
                        >
                          <div
                            v-if="
                              day.diff_reservations.before_ids.includes(
                                reservation.id
                              )
                            "
                          >
                            <div class="my-2 flex flex-col">
                              <div
                                class="mb-2 ml-3 mt-3 inline-flex h-8 w-36 flex-none items-center justify-center rounded-md bg-gray-300 px-2.5 py-0.5 text-sm font-semibold text-white line-through"
                              >
                                {{
                                  formatDatetime(
                                    dayjs(reservation.start_time),
                                    dayjs(reservation.end_time)
                                  )
                                }}
                              </div>
                              <div
                                class="ml-1 flex flex-col pl-2.5 text-sm line-through"
                              >
                                <div
                                  v-if="
                                    reservation.studio_name ||
                                    reservation.resources_name
                                  "
                                >
                                  【{{ reservation.studio_name
                                  }}{{ reservation.resources_name }}】
                                </div>
                                <div
                                  v-if="reservation.project_name"
                                  class="ml-1 font-semibold line-through"
                                >
                                  {{ reservation.project_name }}
                                </div>
                                <div
                                  v-if="reservation.reservation_name"
                                  class="ml-1 line-through"
                                >
                                  {{ reservation.reservation_name }}
                                </div>
                                <div
                                  v-if="reservation.project_customer"
                                  class="ml-1 line-through"
                                >
                                  （{{ reservation.project_customer }}）
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="
                              day.diff_reservations.after_ids.includes(
                                reservation.id
                              )
                            "
                          >
                            <div class="my-2 flex flex-col">
                              <span class="relative inline-block">
                                <div
                                  class="mb-2 ml-3 mt-3 inline-flex h-8 w-36 flex-none items-center justify-center rounded-md bg-green-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                                >
                                  {{
                                    formatDatetime(
                                      dayjs(reservation.start_time),
                                      dayjs(reservation.end_time)
                                    )
                                  }}
                                </div>
                                <span
                                  class="absolute left-0 top-0 transform rounded-full bg-red-400 px-2 py-1 text-xs font-bold leading-none text-white"
                                >
                                  NEW
                                </span>
                              </span>
                              <div class="ml-1 flex flex-col pl-2.5 text-sm">
                                <div
                                  v-if="
                                    reservation.studio_name ||
                                    reservation.resources_name
                                  "
                                >
                                  【{{ reservation.studio_name
                                  }}{{ reservation.resources_name }}】
                                </div>
                                <div
                                  v-if="reservation.project_name"
                                  class="ml-1 font-semibold"
                                >
                                  {{ reservation.project_name }}
                                </div>
                                <div
                                  v-if="reservation.reservation_name"
                                  class="ml-1"
                                >
                                  {{ reservation.reservation_name }}
                                </div>
                                <div
                                  v-if="reservation.project_customer"
                                  class="ml-1"
                                >
                                  （{{ reservation.project_customer }}）
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-else>
                            <div class="my-2 flex flex-col">
                              <div
                                class="mb-2 ml-3 mt-3 inline-flex h-8 w-36 flex-none items-center justify-center rounded-md bg-green-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                              >
                                {{
                                  formatDatetime(
                                    dayjs(reservation.start_time),
                                    dayjs(reservation.end_time)
                                  )
                                }}
                              </div>
                              <div class="ml-1 flex flex-col pl-2.5 text-sm">
                                <div
                                  v-if="
                                    reservation.studio_name ||
                                    reservation.resources_name
                                  "
                                >
                                  【{{ reservation.studio_name
                                  }}{{ reservation.resources_name }}】
                                </div>
                                <div
                                  v-if="reservation.project_name"
                                  class="ml-1 font-semibold"
                                >
                                  {{ reservation.project_name }}
                                </div>
                                <div
                                  v-if="reservation.reservation_name"
                                  class="ml-1"
                                >
                                  {{ reservation.reservation_name }}
                                </div>
                                <div
                                  v-if="reservation.project_customer"
                                  class="ml-1"
                                >
                                  （{{ reservation.project_customer }}）
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          v-if="day.latest_reservations?.length === 0"
                          class="h-32"
                        />
                      </div>
                      <!--週間確定後-->
                      <div
                        v-else-if="
                          data.displayedStatus === 'weekly' &&
                          (day.status === 'weekly_fixed' ||
                            day.weekly_reservations?.length !== 0)
                        "
                      >
                        <div
                          v-for="reservation in day.weekly_reservations"
                          :key="reservation.id"
                        >
                          <div class="my-2 flex flex-col">
                            <div
                              class="mb-2 ml-3 mt-3 inline-flex h-8 w-36 flex-none items-center justify-center rounded-md bg-green-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                            >
                              {{
                                formatDatetime(
                                  dayjs(reservation.start_time),
                                  dayjs(reservation.end_time)
                                )
                              }}
                            </div>
                            <div class="ml-1 flex flex-col pl-2.5 text-sm">
                              <div
                                v-if="
                                  reservation.studio_name ||
                                  reservation.resources_name
                                "
                              >
                                【{{ reservation.studio_name
                                }}{{ reservation.resources_name }}】
                              </div>
                              <div
                                v-if="reservation.project_name"
                                class="ml-1 font-semibold"
                              >
                                {{ reservation.project_name }}
                              </div>
                              <div
                                v-if="reservation.reservation_name"
                                class="ml-1"
                              >
                                {{ reservation.reservation_name }}
                              </div>
                              <div
                                v-if="reservation.project_customer"
                                class="ml-1"
                              >
                                （{{ reservation.project_customer }}）
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          v-if="day.weekly_reservations?.length === 0"
                          class="h-32"
                        />
                      </div>
                      <!--EXCELインポート後-->
                      <div
                        v-else-if="
                          (data.displayedStatus === 'excel' ||
                            data.displayedStatus === 'latest' ||
                            data.displayedStatus === 'weekly') &&
                          importedWork(creator_works, selectedWorkPeriod)
                            ?.imported_at
                        "
                      >
                        <div v-if="day.shift_time_type_id">
                          <div class="my-2 flex gap-x-2">
                            <div
                              class="inline-flex h-28 w-12 flex-none items-center rounded-md bg-blue-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                            >
                              {{ displayShift(day.shift_time_type_id) }}
                            </div>
                            <div class="flex items-center font-semibold">
                              {{ day.name }}
                            </div>
                          </div>
                        </div>
                        <div v-else>
                          <div class="my-2 flex gap-x-2">
                            <div
                              class="inline-flex h-28 w-12 flex-none items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-semibold text-white"
                            ></div>
                          </div>
                        </div>
                      </div>
                      <!--調整中-->
                      <div v-else>
                        <div class="my-2 flex gap-x-2">
                          <!--Lab依頼-->
                          <div
                            :class="[
                              shiftBgColor(day, 'offer_id'),
                              'inline-flex h-12 w-12 flex-none items-center rounded-md px-2.5 py-0.5 text-sm font-semibold text-white'
                            ]"
                          >
                            {{ displayShift(day.offer_id) }}
                          </div>
                          <!--  Labメモ -->
                          <div class="editable-text">
                            {{ day.offer_note }}
                          </div>
                        </div>
                        <div class="my-2 flex gap-x-2">
                          <!-- クリエイター希望-->
                          <div
                            :class="[
                              shiftBgColor(day, 'entry_id'),
                              'inline-flex h-12 w-12 flex-none items-center rounded-md px-2.5 py-0.5 text-sm font-semibold text-white'
                            ]"
                          >
                            {{ displayShift(day.entry_id) }}
                          </div>
                          <!-- クリエイターメモ -->
                          <div class="editable-text">
                            {{ day.entry_note }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-button-lab.p-selectbutton) {
  .p-button.p-highlight {
    background: #ef8e58;
    border-color: #ef8e58;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #ef8e58,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }
}

:deep(.p-button-creator.p-selectbutton) {
  .p-button.p-highlight {
    background: #e66a6a;
    border-color: #e66a6a;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #e66a6a,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }
}

:deep(.p-button-history.p-button.p-button-icon-only.p-button-rounded) {
  height: 1.5rem;
  width: 1.5rem;
}

:deep(.p-inputtext) {
  height: 2rem;
  font-size: 0.75rem;
}
</style>
